/* ==========================================================================
   PanelVehicleRxjs > Client > vehicle > dimensions
   ========================================================================== */
/* Modules */
const { html } = require('common-tags');
const { toNode } = require('@bluekiri/alexandria/helpers/domHelpers');
const { addStyle, addData, randomId } = require('@bluekiri/alexandria/helpers/dataHelpers');

const InputTemplate = require('@bluekiri/alexandria-contrib-components/components/input-rxjs/template');
const InputClient = require('@bluekiri/alexandria-contrib-components/components/input-rxjs/client/js');
const InputActions = require('@bluekiri/alexandria-contrib-components/components/input-rxjs/client/js/store/actions');
const InputConfig = require('@bluekiri/alexandria-contrib-components/components/input-rxjs/client/js/config');

const { validate } = require('../validations');

const { VEHICLE } = require('../../../../../constants');
const configuration = require('../../../config');

function renderDimension(instance, key) {
    const { config = {}, store = {} } = instance;
    const { context = {}, literals = {} } = config;
    const { heightLabel = '', lengthLabel = '', units } = literals;

    /* Data */
    const {
        vehicleHeight = VEHICLE.vehicleHeight,
        vehicleLength = VEHICLE.vehicleLength,
        trailerHeight = VEHICLE.trailerHeight,
        trailerLength = VEHICLE.trailerLength
    } = store.select('data');

    const dimensions = {
        vehicle: {
            height: vehicleHeight,
            length: vehicleLength
        },
        trailer: { height: trailerHeight, length: trailerLength }
    }[key];

    /* Prepare data inputs */
    const dataDimensions = [
        addStyle(
            addData(
                {
                    label: `${lengthLabel} (${units})`,
                    value: String(dimensions.length)
                },
                { type: randomId(randomId(configuration().type.dimensions)) }
            ),
            [InputConfig().css.withBorder]
        ),
        addStyle(
            addData(
                {
                    label: `${heightLabel} (${units})`,
                    value: String(dimensions.height)
                },
                { type: randomId(randomId(configuration().type.dimensions)) }
            ),
            [InputConfig().css.withBorder]
        )
    ];

    /* Inputs(length/height) template */
    const inputs = dataDimensions.map(input => InputTemplate(input, context));

    return html(inputs);
}

function addDimesions(instance, key) {
    const result = instance;
    const { vehicles, trailer } = instance;

    const hook = {
        vehicle: configuration().hooks.vehicleDimensions,
        trailer: configuration().hooks.trailerDimensions
    }[key];

    const siblingNode = {
        vehicle: vehicles.element,
        trailer: trailer ? instance.trailer.element : false
    }[key];

    const template = html(`<div class="vehicle-panel__dimensions" ${hook}>${renderDimension(instance, key)}</div>`);

    const node = toNode(template)[0];

    result.hooks = {
        ...result.hooks,
        [`${key}Dimensions`]: node
    };

    /* Append as a sibling node */
    siblingNode.insertAdjacentElement('afterend', node);

    /* Init inputs dimensions height and length */
    initDimensions(instance, key);

    return instance;
}

function initVehicleDimensions(instance) {
    const result = instance;
    const { vehicleInput = {} } = instance;
    const { panel } = vehicleInput;

    if (!panel) return;

    /* Data Panel */
    const { panelType } = vehicleInput.store.select('data');
    const panelSelector = `[data-type="${panelType}"]`;

    /* Init inputs */
    result.vehicleDimensions = [
        ...panel.element.querySelectorAll(
            `${panelSelector} [${configuration().hooks.vehicleDimensions}] [data-component="InputRxjs"]`
        )
    ].map(input => {
        const { type } = input.dataset;
        return InputClient(`${panelSelector} [data-component="InputRxjs"][data-type="${type}"]`);
    });

    if (result.vehicleDimensions.length !== 2) return;

    /* Extends reactions */
    result.reactions = {
        ...result.reactions,
        vehicleDimensions: result.vehicleDimensions.map((input, ind) => ({
            ...input.store.subscribe((state, action) => {
                if (action.type !== InputActions.types.CHANGE_VALUE) return;

                /* Validate vehicle dimensions */
                validate(instance);

                /* Data */
                const { value } = input.store.select('input');
                const { hasError } = instance.store.select('data');

                /* Sync store */
                if (hasError) return;

                if (ind === 0) instance.actions.changeVehicle({ vehicleLength: value });
                if (ind === 1) instance.actions.changeVehicle({ vehicleHeight: value });
            })
        }))
    };
}

function initTrailerDimensions(instance) {
    const result = instance;
    const { vehicleInput = {} } = instance;
    const { panel } = vehicleInput;

    if (!panel) return;

    /* Data Panel */
    const { panelType } = vehicleInput.store.select('data');
    const panelSelector = `[data-type="${panelType}"]`;

    /* Init inputs */
    result.trailerDimensions = [
        ...panel.element.querySelectorAll(
            `${panelSelector} [${configuration().hooks.trailerDimensions}] [data-component="InputRxjs"]`
        )
    ].map(input => {
        const { type } = input.dataset;
        return InputClient(`${panelSelector} [data-component="InputRxjs"][data-type="${type}"]`);
    });

    if (result.trailerDimensions.length !== 2) return;

    /* Extends reactions */
    result.reactions = {
        ...result.reactions,
        trailerDimensions: result.trailerDimensions.map((input, ind) => ({
            ...input.store.subscribe((state, action) => {
                if (action.type !== InputActions.types.CHANGE_VALUE) return;

                /* Validate trailer dimensions */
                validate(instance);

                /* Data */
                const { value } = input.store.select('input');
                const { hasError } = instance.store.select('data');

                /* Sync store */
                if (hasError) return;

                if (ind === 0) instance.actions.changeVehicle({ trailerLength: value });
                if (ind === 1) instance.actions.changeVehicle({ trailerHeight: value });
            })
        }))
    };
}

function initDimensions(instance, key) {
    if (key === 'vehicle') initVehicleDimensions(instance);
    if (key === 'trailer') initTrailerDimensions(instance);
}

function destroyDimensions(instance, key) {
    const result = instance;
    const inputs = result[`${key}Dimensions`];

    if (!inputs || inputs.length === 0) return;

    /* Destroy components */
    inputs.forEach(input => {
        input.actions.destroy();
    });

    /* Unsubscribe */
    instance.reactions[`${key}Dimensions`].forEach(reaction => reaction.unsubscribe());

    /* Remove hooks */
    instance.hooks[`${key}Dimensions`].remove();

    /* Remove references */
    delete result[`${key}Dimensions`];
    delete result.reactions[`${key}Dimensions`];
    delete result.hooks[`${key}Dimensions`];
}

module.exports = {
    addDimesions,
    destroyDimensions
};
